import { useEffect, useState } from "react";
import { AlarmClock, BadgeCheck, Banknote, CircleCheck, Clock4, Crown, FileImage, Paperclip, Ticket, Upload, X } from "lucide-react";
import { API, api, money } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "—");

function UsageBar({ label, used, max, testid }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  return (
    <div className="usage-row" data-testid={testid}>
      <div className="usage-top"><span>{label}</span><strong>{used}{max > 0 ? ` / ${max}` : " · tanpa batas"}</strong></div>
      <div className="aging-bar">{max > 0 && <i className={pct >= 100 ? "red" : pct >= 80 ? "amber" : "teal"} style={{ width: `${pct}%` }} />}</div>
    </div>
  );
}

export default function Subscription({ locked }) {
  const [sub, setSub] = useState(null);
  const [pick, setPick] = useState(null);
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState("");
  const [proof, setProof] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const notify = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 6000); };
  const load = () => api("/subscription").then(setSub).catch((e) => notify(e.message, "error"));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!sub) return <div className="report-loading" data-testid="subscription-loading">Memuat paket...</div>;
  const openPick = (p) => { setPick({ ...p, code: Math.floor(100 + Math.random() * 900) }); setMonths(1); setNote(""); setProof(null); };
  const onProofFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp", "application/pdf"].includes(f.type)) return notify("Bukti transfer harus PNG, JPG, WEBP, atau PDF", "error");
    if (f.size > 3 * 1024 * 1024) return notify("Ukuran bukti transfer maksimal 3 MB", "error");
    const r = new FileReader();
    r.onload = () => setProof({ data: r.result.split(",")[1], mime: f.type, name: f.name, preview: f.type.startsWith("image/") ? r.result : null });
    r.readAsDataURL(f);
    e.target.value = "";
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api("/subscription/request", { method: "POST", body: JSON.stringify({ package_name: pick.name, months: Number(months), note, unique_code: pick.code, proof_data: proof.data, proof_mime: proof.mime, proof_name: proof.name }) });
      setPick(null); setNote(""); setMonths(1); setProof(null);
      notify("Permintaan terkirim — tim kami akan mengonfirmasi pembayaran Anda maks. 1x24 jam kerja.");
      load();
    } catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div data-testid="subscription-page">
      {message && <div data-testid="subscription-message" className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="page-heading">
        <div>
          <p className="eyebrow">LANGGANAN</p>
          <h1>Paket & Masa Aktif</h1>
          <p className="muted">Pantau pemakaian dan tingkatkan paket sesuai kebutuhan bisnis Anda.</p>
        </div>
      </div>
      {locked && <div className="sub-locked" data-testid="subscription-locked"><AlarmClock size={17} /><div><b>Masa aktif Anda telah berakhir.</b><p>Akses modul dibekukan sementara. Pilih paket di bawah dan lakukan pembayaran untuk mengaktifkan kembali seluruh fitur — data Anda tetap aman.</p></div></div>}
      <div className="content-grid sub-grid">
        <section className="surface sub-status" data-testid="subscription-status">
          <div className="sub-status-head">
            <span className="pw-icon"><Crown size={19} /></span>
            <div>
              <small>Paket aktif</small>
              <h3>{sub.label} <span className="mod-chip all">{sub.plan}</span></h3>
            </div>
            {sub.days_left !== null && (
              <div className={`sub-countdown ${sub.days_left <= 2 ? "urgent" : ""}`} data-testid="subscription-countdown">
                <strong>{sub.days_left}</strong><small>hari lagi</small>
              </div>
            )}
          </div>
          <p className="muted sub-until">{sub.expires_at ? `${sub.is_trial ? "Masa trial" : "Masa aktif"} berakhir ${fmtDate(sub.expires_at)}` : "Masa aktif tanpa batas waktu"}</p>
          <UsageBar label="Pengguna" used={sub.usage.users} max={sub.usage.max_users} testid="usage-users" />
          <UsageBar label="Invoice bulan ini" used={sub.usage.invoices_month} max={sub.usage.max_invoices} testid="usage-invoices" />
        </section>
        <section className="surface sub-bank" data-testid="subscription-bank">
          <div className="section-head"><div><h3><Banknote size={16} /> Pembayaran via transfer</h3><p className="muted">Konfirmasi manual oleh tim AIC</p></div></div>
          <div className="bank-box">
            <small>{sub.bank_info.bank} — a.n. {sub.bank_info.account_name}</small>
            <strong data-testid="bank-account-number">{sub.bank_info.account_number}</strong>
          </div>
          <p className="muted bank-note">{sub.bank_info.instruction}</p>
          {sub.pending && (
            <div className="pending-box" data-testid="pending-request">
              <Clock4 size={15} />
              <div>
                <b>Menunggu konfirmasi tim</b>
                <p>{sub.pending.number} · {sub.pending.package_name} {sub.pending.months} bulan · {money(sub.pending.amount)}{sub.pending.unique_code ? ` (kode unik ${sub.pending.unique_code})` : ""}</p>
                {sub.pending.proof_mime && <a data-testid="pending-proof-link" className="proof-link" href={`${API}/subscription/request/${sub.pending.id}/proof`} target="_blank" rel="noreferrer"><Paperclip size={11} /> Lihat bukti transfer{sub.pending.proof_name ? ` — ${sub.pending.proof_name}` : ""}</a>}
              </div>
              {sub.can_request && <button data-testid="cancel-request-button" className="link-btn" onClick={() => api(`/subscription/request/${sub.pending.id}`, { method: "DELETE" }).then(() => { notify("Permintaan dibatalkan"); load(); }).catch((e) => notify(e.message, "error"))}>Batalkan</button>}
            </div>
          )}
        </section>
      </div>
      <h3 className="sub-section-title">Pilih paket</h3>
      <div className="pkg-grid" data-testid="subscription-packages">
        {sub.packages.map((p, i) => {
          const current = p.name === sub.plan;
          return (
            <div key={p.id} className={`surface pkg-card ${current ? "current" : ""}`} data-testid={`sub-package-${i}`}>
              <div className="pkg-head"><div><span className="mod-chip all">{p.name}</span><h3>{p.label}</h3></div>{current && <BadgeCheck size={19} className="pkg-current-ic" />}</div>
              <div className="pkg-price">{p.price_monthly > 0 ? <>{money(p.price_monthly)}<small>/bulan</small></> : "Gratis"}</div>
              <div className="pkg-limits">
                <span>{p.max_users > 0 ? `Maks ${p.max_users} pengguna` : "Pengguna tanpa batas"}</span>
                <span>{p.max_invoices > 0 ? `Maks ${p.max_invoices} invoice/bln` : "Invoice tanpa batas"}</span>
              </div>
              <ul className="pkg-features-list">{(p.features || []).map((f) => <li key={f}><CircleCheck size={12} /> {f}</li>)}</ul>
              <div className="pkg-foot">
                {current ? <span className="status paid">Paket saat ini</span>
                  : sub.can_request ? <button data-testid={`choose-package-${i}`} className="primary-btn nowrap" disabled={Boolean(sub.pending) || p.price_monthly <= 0} onClick={() => openPick(p)}>Pilih Paket</button>
                  : <small className="upgrade-hint">Hubungi pemilik untuk upgrade</small>}
              </div>
            </div>
          );
        })}
      </div>
      {pick && (
        <div className="modal-overlay" data-testid="upgrade-modal" onClick={() => setPick(null)}>
          <div className="modal-panel settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-head">
              <div><p className="eyebrow">UPGRADE PAKET</p><h2>{pick.label} — {money(pick.price_monthly)}/bulan</h2>
                <p className="muted sm-sub">Transfer ke {sub.bank_info.bank} {sub.bank_info.account_number} a.n. {sub.bank_info.account_name}, lalu kirim permintaan ini.</p></div>
              <button className="icon-btn" onClick={() => setPick(null)}><X size={18} /></button>
            </div>
            <div className="sm-body">
              <div className="tax-form-grid">
                <label>Durasi
                  <select data-testid="upgrade-months-select" value={months} onChange={(e) => setMonths(e.target.value)}>
                    {[1, 3, 6, 12].map((m) => <option key={m} value={m}>{`${m} bulan`}</option>)}
                  </select>
                </label>
                <label>Subtotal paket<input value={money(pick.price_monthly * Number(months))} readOnly /></label>
              </div>
              <div className="unique-code-box" data-testid="upgrade-unique-code">
                <span className="uc-ic"><Ticket size={16} /></span>
                <div>
                  <small>Kode unik transfer</small>
                  <b>{pick.code}</b>
                </div>
                <div className="uc-total">
                  <small>Total yang harus ditransfer</small>
                  <strong data-testid="upgrade-total">{money(pick.price_monthly * Number(months) + pick.code)}</strong>
                </div>
              </div>
              <p className="uc-hint">Transfer <b>tepat sesuai total di atas</b> — 3 digit terakhir (kode unik) membantu tim kami memverifikasi pembayaran Anda lebih cepat.</p>
              <p className="sm-field-label">Bukti transfer <span className="muted-inline">— wajib, PNG/JPG/WEBP/PDF maks. 3 MB</span></p>
              {proof ? (
                <div className="proof-attached" data-testid="proof-attached">
                  {proof.preview ? <img src={proof.preview} alt="Bukti transfer" /> : <span className="proof-pdf-ic"><FileImage size={18} /></span>}
                  <div><b>{proof.name}</b><small>Bukti transfer terlampir</small></div>
                  <button data-testid="proof-remove-button" className="icon-btn" onClick={() => setProof(null)}><X size={15} /></button>
                </div>
              ) : (
                <label className="proof-drop" data-testid="proof-upload-label">
                  <Upload size={17} />
                  <span><b>Unggah bukti transfer</b><small>Klik untuk memilih file dari perangkat Anda</small></span>
                  <input data-testid="proof-file-input" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={onProofFile} hidden />
                </label>
              )}
              <label className="cp-full">Catatan pembayaran (opsional)<input data-testid="upgrade-note-input" className="upgrade-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. transfer 13 Agu a.n. Rahmat" /></label>
            </div>
            <div className="sm-foot">
              <button className="secondary-btn" onClick={() => setPick(null)}>Batal</button>
              <button data-testid="upgrade-submit-button" className="primary-btn" disabled={saving || !proof} onClick={submit}>{saving ? "Mengirim..." : "Kirim Permintaan Upgrade"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
